/**
 * The props that stop a password manager treating a filter box as a login field.
 *
 * Every search input in the app narrows a list, but they read to LastPass/1Password like identity
 * fields — the placeholders say "name", "email", "phone" — so the extensions offer to fill them and
 * park an icon inside the field, on top of the text. This kept having to be rediscovered per call
 * site, so it lives here once.
 *
 * `type="search"` is the part that actually works: the managers skip search inputs outright, where
 * `data-lpignore` only suppresses autofill and is honoured inconsistently between versions. The
 * attributes are belt-and-braces, and `name` keeps the heuristics off an otherwise unnamed box.
 * `type="search"` also brings WebKit's own clear button, which is hidden because these fields carry
 * their own.
 *
 * @param name - A stable, non-identity-shaped input name (e.g. "team-roster-search").
 */
export function filterSearchHtmlInputProps(name: string) {
	return {
		name,
		autoComplete: "off",
		"data-lpignore": "true",
		"data-1p-ignore": "true",
		"data-form-type": "other",
	};
}

/** Hides WebKit's native search clear button, since these fields carry their own. */
export const FILTER_SEARCH_CANCEL_BUTTON_SX = { "& input::-webkit-search-cancel-button": { display: "none" } } as const;

/**
 * Spread onto a `SystemValidationTextField` that filters a list.
 *
 * Use the two exports above instead when the field already passes its own `inputSx` or
 * `injectedProps` — spreading this would replace them rather than merge.
 */
export function filterSearchFieldProps(name: string) {
	return {
		inputType: "search" as const,
		inputSx: FILTER_SEARCH_CANCEL_BUTTON_SX,
		injectedProps: { slotProps: { htmlInput: filterSearchHtmlInputProps(name) } },
	};
}
